// Cliente HTTP central com interceptor de refresh token (SEF-70).
//
// Contrato de erro do backend: { error: { code, details? } }.
// Em 401 numa rota autenticada, tenta POST /api/auth/refresh uma única vez
// (deduplicando chamadas concorrentes) e refaz a requisição original. Se o
// refresh falhar, limpa o estado e dispara `window.onSessaoExpirada()` para
// o App redirecionar ao login.

import {
  getAccessToken,
  getRefreshToken,
  atualizarTokens,
  limparAuth,
} from './auth';

// baseURL configurável (SEF-137). Default '' = same-origin (status quo da
// SEF-70). Defina REACT_APP_API_URL quando front e back rodarem em origens
// diferentes (ex.: dev com CRA em :3000 e backend em :8080). Sem barra final.
const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// Junta a baseURL ao path. Paths são sempre relativos começando com '/'.
function montarUrl(path) {
  return `${API_BASE_URL}${path}`;
}

export class ApiError extends Error {
  constructor(status, code, details) {
    super(code || `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code || null;
    this.details = details || null;
  }
}

async function extrairErro(resposta) {
  let code = null;
  let details = null;
  try {
    const corpo = await resposta.json();
    code = corpo?.error?.code ?? null;
    details = corpo?.error?.details ?? null;
  } catch {
    // resposta sem corpo JSON (ex.: 429 sem body, 204) — mantém code nulo
  }
  return new ApiError(resposta.status, code, details);
}

let refreshEmAndamento = null;

async function tentarRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  // Deduplica refresh concorrente: várias requisições que tomam 401 ao
  // mesmo tempo compartilham a mesma promessa.
  if (!refreshEmAndamento) {
    refreshEmAndamento = (async () => {
      try {
        const resp = await fetch(montarUrl('/api/auth/refresh'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (!resp.ok) return false;
        const dados = await resp.json();
        atualizarTokens({
          accessToken: dados.accessToken,
          refreshToken: dados.refreshToken,
        });
        return true;
      } catch {
        return false;
      } finally {
        // libera o lock: chamadas concorrentes já capturaram esta promessa
        // antes do finally rodar (sincrono até o primeiro await), então
        // zerar aqui não as afeta e permite novas tentativas depois.
        refreshEmAndamento = null;
      }
    })();
  }
  return refreshEmAndamento;
}

function montarHeaders(headers, comAuth, temCorpo) {
  const base = new Headers(headers || {});
  if (temCorpo && !base.has('Content-Type')) {
    base.set('Content-Type', 'application/json');
  }
  if (comAuth) {
    const token = getAccessToken();
    if (token) base.set('Authorization', `Bearer ${token}`);
  }
  return base;
}

/**
 * Faz uma requisição à API.
 *
 * @param {string} path  caminho relativo (ex.: '/api/auth/login')
 * @param {object} opts  { method, body, headers, auth=true, retry=true }
 *   - body: objeto serializado como JSON automaticamente (string passa direto)
 *   - auth: anexa Authorization se houver token
 *   - retry: habilita o fluxo de refresh+retry em 401
 *   - responseType: 'json' (default) ou 'blob' (download autenticado de binário)
 * @returns {Promise<any|Blob|null>} JSON do corpo, Blob se responseType='blob',
 *   ou null para 204/sem corpo
 * @throws {ApiError} em respostas não-ok
 */
export async function apiFetch(path, opts = {}) {
  const {
    method = 'GET',
    body,
    headers,
    auth = true,
    retry = true,
    responseType = 'json',
  } = opts;
  const temCorpo = body !== undefined && body !== null;
  const corpoSerializado =
    temCorpo && typeof body !== 'string' ? JSON.stringify(body) : body;

  const fazer = () =>
    fetch(montarUrl(path), {
      method,
      headers: montarHeaders(headers, auth, temCorpo),
      body: temCorpo ? corpoSerializado : undefined,
    });

  let resposta = await fazer();

  if (resposta.status === 401 && retry && auth && getRefreshToken()) {
    const renovou = await tentarRefresh();
    if (renovou) {
      resposta = await fazer();
    } else {
      limparAuth();
      if (typeof window.onSessaoExpirada === 'function') {
        window.onSessaoExpirada();
      }
      throw await extrairErro(resposta);
    }
  }

  if (!resposta.ok) {
    throw await extrairErro(resposta);
  }

  if (resposta.status === 204) return null;
  if (responseType === 'blob') return resposta.blob();
  const texto = await resposta.text();
  if (!texto) return null;
  try {
    return JSON.parse(texto);
  } catch {
    return texto;
  }
}
